import { useEffect, useRef, useState } from 'react'
import { LocalAudioTrack } from 'livekit-client'

import {
  getAudioCaptureOptionsForNoiseFilter,
  getNoiseFilterUnsupportedReason,
  logNoiseFilterEvent,
  type NoiseFilterApplyStatus,
  type NoiseFilterMode,
} from '@/src/lib/noiseFilter'
import { ServerNoiseFilterProcessor } from '@/src/lib/serverNoiseFilterProcessor'

interface UseNoiseFilterProcessorOptions {
  mode: NoiseFilterMode
  /**
   * Локальный аудиотрек; без него процессор не к чему вешать.
   */
  track: LocalAudioTrack | undefined
  enabled?: boolean
  /**
   * Адрес ноды из start_server. С него собираем `wss://хост/nf`.
   */
  livekitUrl: string | null
  /**
   * JWT комнаты. После впуска из зала ожидания должен быть уже повышенный.
   */
  accessToken: string | null
  /**
   * Вызывается, когда сокет не открылся или оборвался — режим тихо ставим в `off`.
   */
  onFallbackToNone?: () => void
}

/**
 * Вешает серверный шумодав на опубликованный LocalAudioTrack или снимает процессор.
 *
 * Вход в звонок не блокируем: процессор подключается асинхронно. Если сервис
 * на ноде недоступен, откатываемся на сырой микрофон без отдельного тоста.
 */
export function useNoiseFilterProcessor({
  mode,
  track,
  enabled = true,
  livekitUrl,
  accessToken,
  onFallbackToNone,
}: UseNoiseFilterProcessorOptions) {
  const [status, setStatus] = useState<NoiseFilterApplyStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const applyGenerationRef = useRef(0)
  const onFallbackRef = useRef(onFallbackToNone)
  onFallbackRef.current = onFallbackToNone

  const unsupportedReason = getNoiseFilterUnsupportedReason(mode)

  useEffect(() => {
    if (!enabled || !track || !(track instanceof LocalAudioTrack)) {
      setStatus('idle')
      setErrorMessage(null)
      return
    }

    if (unsupportedReason) {
      setStatus('unsupported')
      setErrorMessage(unsupportedReason)
      onFallbackRef.current?.()
      return
    }

    const generation = ++applyGenerationRef.current
    let cancelled = false

    const apply = async () => {
      setStatus('pending')
      setErrorMessage(null)

      try {
        const currentProcessor = track.getProcessor()

        if (currentProcessor) {
          await track.stopProcessor()
        }

        const captureOptions = getAudioCaptureOptionsForNoiseFilter(mode)
        await track.restartTrack(captureOptions)

        if (cancelled || generation !== applyGenerationRef.current) {
          return
        }

        if (mode === 'off') {
          setStatus('ready')
          logNoiseFilterEvent('processor_off')
          return
        }

        if (!livekitUrl || !accessToken) {
          setStatus('error')
          setErrorMessage('Нет адреса ноды или токена для шумодава')
          logNoiseFilterEvent('processor_missing_session')
          onFallbackRef.current?.()
          return
        }

        const processor = new ServerNoiseFilterProcessor({
          livekitUrl,
          accessToken,
          onFatal: () => {
            if (cancelled || generation !== applyGenerationRef.current) {
              return
            }

            onFallbackRef.current?.()
          },
        })

        await track.setProcessor(processor)

        if (cancelled || generation !== applyGenerationRef.current) {
          await track.stopProcessor().catch(() => undefined)
          return
        }

        setStatus('ready')
        logNoiseFilterEvent('processor_on')
      } catch (error) {
        if (cancelled || generation !== applyGenerationRef.current) {
          return
        }

        await track.stopProcessor().catch(() => undefined)

        setStatus('error')
        setErrorMessage('Не удалось включить шумодав')
        logNoiseFilterEvent('processor_error', {
          message: error instanceof Error ? error.message : String(error),
        })
        onFallbackRef.current?.()
      }
    }

    apply()

    return () => {
      cancelled = true
    }
  }, [accessToken, enabled, livekitUrl, mode, track, unsupportedReason])

  return {
    status,
    errorMessage,
    unsupportedReason,
  }
}
